/**
 * 按钮组件
 *
 * 提供多种按钮变体、尺寸和状态的可复用按钮组件
 * 参考 flowva.top 现代化设计风格
 */

import React from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * 加载图标组件
 */
const LoadingIcon: React.FC = () => (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
      fill="none"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** 按钮变体 */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'agent';
  /** 按钮尺寸 */
  size?: 'sm' | 'md' | 'lg';
  /** 是否加载中 */
  loading?: boolean;
  /** 左侧图标 */
  leftIcon?: React.ReactNode;
  /** 右侧图标 */
  rightIcon?: React.ReactNode;
  /** 是否全宽度 */
  fullWidth?: boolean;
  /** 无障碍标签（用于按钮仅包含图标时） */
  ariaLabel?: string;
}

export const Button: React.FC<ButtonProps> = React.memo(({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className,
  children,
  type = 'button',
  ariaLabel,
  ...props
}) => {
  // 计算按钮的基础样式 - 参考 flowva.top 风格
  const baseClasses = clsx(
    'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-150',
    'focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
    'active:scale-[0.98]'
  );

  // 变体样式 - 现代化设计（Agent 靛蓝色系）
  const variantClasses = {
    primary: clsx(
      'bg-ink-800 text-white',
      'shadow-xs hover:shadow-sm',
      'hover:bg-ink-700',
      'active:scale-[0.98]',
      'focus:ring-paper-500 focus:ring-offset-gray-50'
    ),
    secondary: clsx(
      'bg-white text-gray-700 border border-gray-200',
      'hover:bg-gray-50 hover:border-gray-300',
      'active:scale-[0.98]',
      'focus:ring-gray-500'
    ),
    ghost: clsx(
      'bg-transparent text-gray-600',
      'hover:bg-gray-100 hover:text-gray-900',
      'active:scale-[0.98]',
      'focus:ring-gray-500'
    ),
    danger: clsx(
      'bg-error-500 text-white',
      'shadow-xs hover:shadow-sm',
      'hover:bg-error-600',
      'active:scale-[0.98]',
      'focus:ring-error-500'
    ),
    agent: clsx(
      'bg-ink-800 text-white',
      'shadow-xs hover:shadow-sm',
      'hover:bg-ink-700',
      'active:scale-[0.98]',
      'focus:ring-paper-500 focus:ring-offset-gray-50'
    )
  };

  // 尺寸样式
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2'
  };

  // 全宽度样式
  const fullWidthClass = fullWidth ? 'w-full' : '';

  // 合并所有类名
  const buttonClasses = twMerge(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    fullWidthClass,
    className
  );

  // 确定aria-label
  const getAriaLabel = (): string | undefined => {
    if (ariaLabel) {
      return ariaLabel;
    }
    if (loading) {
      return '加载中';
    }
    if (typeof children === 'string') {
      return children;
    }
    return undefined;
  };

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled || loading}
      aria-busy={loading}
      aria-label={getAriaLabel()}
      {...props}
    >
      {loading && <LoadingIcon />}
      {!loading && leftIcon && <span className="flex items-center">{leftIcon}</span>}
      {children}
      {!loading && rightIcon && <span className="flex items-center">{rightIcon}</span>}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
